/**
 * Importar cardápio por OCR — assistente conversacional.
 *
 * 1. Upload: fotos/PDF normalizados no client (canvas → JPEG ~1600px; PDF
 *    renderizado página a página com pdfjs-dist sob demanda) — o servidor só
 *    recebe páginas JPEG.
 * 2. Chat: a IA lê as páginas e conversa NO IDIOMA DO PAINEL — pede
 *    confirmação de idioma/elementos ambíguos e pede foto nova quando a
 *    qualidade está ruim (substituição direta da página).
 * 3. Revisão: rascunho editável (idioma, restaurante, categorias/produtos,
 *    extras/variações/ingredientes, fotos recortadas) → commit cria o
 *    cardápio no idioma nativo e dispara a tradução.
 */
import { useEffect, useRef, useState } from 'react'
import type { PreparedPage } from './import/prepare.js'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { importsApi } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { Button, toast } from '../components/ui/index.js'

import { prepareFiles } from './import/prepare.js'
import { OutcomePanel } from './import/OutcomePanel.js'
import { ImportChat } from './import/ImportChat.js'
import { ReviewLoading } from './import/ReviewLoading.js'
// #489 (item 5): Fase 3 extraída por responsabilidade (R3) — o cartão de
// conclusão ganhou as contagens de pausados do commit (role="status")
import { CommittedCard } from './import/CommittedCard.js'
// #491 (PR-2): secção «Continue de onde parou» — importações retomáveis
// (componente extraído, R3; a página só monta e trata o foco pós-clique)
import { ResumeImports } from './import/ResumeImports.js'
// ─── Página ───────────────────────────────────────────────────────────────────

export function ImportMenu() {
  const { t, lang } = useI18n()
  const { slug } = useParams()
  const [pages, setPages] = useState<PreparedPage[]>([])
  // #278: o id vive na URL (?id=) — sobrevive a reload e o commit mostra a
  // tela de concluído em vez de voltar ao formulário vazio
  const [searchParams, setSearchParams] = useSearchParams()
  const importId = searchParams.get('id')
  const setImportId = (id: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (id) next.set('id', id)
        else next.delete('id')
        return next
      },
      { replace: !id },
    )
  const [preparing, setPreparing] = useState(false)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)
  const [link, setLink] = useState('')
  const [linkBusy, setLinkBusy] = useState(false)

  const { data: view } = useQuery({
    queryKey: ['menu-import', importId],
    queryFn: () => importsApi.get(importId!),
    enabled: !!importId,
    refetchInterval: (q) => {
      const st = q.state.data?.status ?? ''
      // trabalho pesado (OCR/ajustes): poll agressivo p/ atividade ao vivo.
      // PR-A.5: em extracted os badges do enriquecimento assíncrono chegam
      // segundos depois — poll fino para a tela os pegar cedo
      if (st === 'processing') return 1000
      if (st === 'reviewing') return 1500
      return st === 'extracted' ? 1200 : false
    },
  })

  // #491 (PR-2): clique em «Continuar importação» — o foco vai para o título
  // da vista que abre (a11y, a página muda de fase sem navegar). Só quando o
  // id veio DA LISTA: reabrir por URL ou recarregar não mexe no foco. O h1 só
  // existe depois do GET /:id responder — sem título ainda, o efeito re-corre
  // com `view` (import committed não tem o h1: sem foco, e está tudo bem).
  const resumedFromList = useRef(false)
  useEffect(() => {
    if (!importId || !resumedFromList.current) return
    const title = document.querySelector<HTMLElement>('[data-testid="import-title"]')
    if (!title) return
    resumedFromList.current = false
    title.focus()
  }, [importId, view])
  const resumeFromList = (id: string) => {
    resumedFromList.current = true
    setImportId(id)
  }

  const uploadMut = useMutation({
    mutationFn: () =>
      importsApi.upload(
        pages.map((p) => p.blob),
        lang,
      ),
    onSuccess: (data) => setImportId(data.id),
    onError: (e: Error) => toast.error(e.message),
  })

  const onImportLink = async () => {
    const url = link.trim()
    if (!url) return
    setLinkBusy(true)
    try {
      const { id } = await importsApi.fromUrl(url, lang)
      setImportId(id)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e))
    } finally {
      setLinkBusy(false)
    }
  }

  const onPick = async (files: FileList | null) => {
    if (!files?.length) return
    setPreparing(true)
    const prepared = await prepareFiles([...files])
    setPages((prev) => [...prev, ...prepared].slice(0, 20))
    setPreparing(false)
  }

  // ── Fase 1: upload ──
  if (!importId) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.import.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('admin.import.subtitle')}</p>
        </div>

        {/* #491 (PR-2): importações retomáveis — entre o cabeçalho e o cartão
            do link (UX §1); com a lista vazia/erro a secção não monta */}
        <ResumeImports onResume={resumeFromList} />

        {/* ── Cardápio digital por link (leitura no servidor, sem foto) ── */}
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-2">
          <p className="text-sm font-bold text-gray-900">🔗 {t('admin.import.link_title')}</p>
          <div className="flex gap-2">
            <input
              data-testid="import-link-input"
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void onImportLink()}
              placeholder="https://restaurante.pt/cardapio"
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
            />
            <Button
              loading={linkBusy}
              disabled={!link.trim()}
              data-testid="import-link-submit"
              onClick={() => void onImportLink()}
            >
              {t('admin.import.link_button')}
            </Button>
          </div>
          <p className="text-xs text-gray-400">{t('admin.import.link_hint')}</p>
        </div>

        <div
          role="button"
          tabIndex={0}
          data-testid="import-dropzone"
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => e.key === 'Enter' && fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            void onPick(e.dataTransfer.files)
          }}
          className={`w-full rounded-2xl border-2 border-dashed p-10 text-center transition-colors cursor-pointer ${
            dragging
              ? 'border-orange-500 bg-orange-100'
              : 'border-gray-300 bg-gray-50 hover:border-orange-400 hover:bg-orange-50'
          }`}
        >
          <p className="text-sm font-medium text-gray-600">
            {preparing ? '…' : t('admin.import.drop_hint')}
          </p>
        </div>
        <input
          data-testid="import-file-input"
          ref={fileInput}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/avif,application/pdf"
          className="hidden"
          onChange={(e) => void onPick(e.target.files)}
        />
        {pages.length > 0 && (
          <>
            <p className="text-sm text-gray-500">
              {pages.length} {t('admin.import.pages')}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {pages.map((p, i) => (
                <div
                  key={i}
                  data-testid={`import-page-${i}`}
                  className="relative rounded-lg overflow-hidden border border-gray-200"
                >
                  <img src={p.preview} alt={`p${i + 1}`} className="w-full h-24 object-cover" />
                  <button
                    type="button"
                    data-testid={`import-page-remove-${i}`}
                    onClick={() => setPages((prev) => prev.filter((_, j) => j !== i))}
                    className="absolute top-1 right-1 rounded-full bg-black/60 text-white text-xs w-5 h-5"
                  >
                    ×
                  </button>
                  <span className="absolute bottom-1 left-1 rounded bg-black/60 text-white text-[10px] px-1">
                    {i + 1}
                  </span>
                </div>
              ))}
            </div>
            <Button
              loading={uploadMut.isPending}
              data-testid="import-upload-submit"
              onClick={() => uploadMut.mutate()}
            >
              {t('admin.import.send')}
            </Button>
          </>
        )}
      </div>
    )
  }

  if (!view) {
    // #486 (item 3): revisão a carregar — o cartão de idioma e moeda já
    // existe com indicador e aria-busy, e «Importar» fica desativado
    // (antes: spinner solto, sem cartão e sem botão)
    return (
      <div className="max-w-2xl mx-auto py-8">
        <ReviewLoading />
      </div>
    )
  }

  // ── Fase 3: concluído ──
  if (view.status === 'committed') {
    return <CommittedCard view={view} slug={slug} />
  }

  // ── Fase 2: chat-first com outcome ao lado (parte B) ──
  return (
    <div className="max-w-6xl mx-auto space-y-4">
      <div>
        {/* #491 (PR-2): alvo do foco pós-clique «Continuar importação» —
            tabIndex={-1} deixa o h1 receber focus() sem entrar na tabagem */}
        <h1
          data-testid="import-title"
          tabIndex={-1}
          className="text-2xl font-bold text-gray-900 focus:outline-none"
        >
          {t('admin.import.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1" data-testid="import-subtitle">
          {view.status === 'processing' && view.awaiting !== 'none'
            ? // #301: pausado esperando o dono — não "Lendo… 12 de 12"
              t('admin.import.awaiting_title')
            : view.status === 'processing'
              ? `${t('admin.import.processing')} — ${t('admin.import.page_progress', {
                  n: String(Math.min(view.processed_pages + 1, view.page_count)),
                  total: String(view.page_count),
                })}`
              : t('admin.import.review_title')}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <ImportChat view={view} />
        <OutcomePanel
          view={view}
          onCancelled={() => {
            setImportId(null)
            setPages([])
          }}
        />
      </div>
    </div>
  )
}
